/**
 * PromotionSuggestionsPanel — onboarding-on-the-fly UI.
 *
 * A13-N14. Shows pending promotion suggestions:
 * "Du hast Fendt 312 5x erwahnt — als Maschine anlegen?"
 * One-click accept/reject.
 *
 * Props:
 *  - embedded: boolean (if true, shorter empty message)
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../lib/apiClient';

const API_PREFIX = '/api/v1/aif/brain-dump';

const ENTITY_ICONS = {
  field: '\uD83C\uDF3E', cow: '\uD83D\uDC04', pig: '\uD83D\uDC16',
  machine: '\uD83D\uDE9C', building: '\uD83C\uDFE0', crop: '\uD83C\uDF31',
  supplier: '\uD83C\uDFE2',
};

export default function PromotionSuggestionsPanel({ embedded = false }) {
  const { t } = useTranslation();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`${API_PREFIX}/promotions?status=pending`);
      const data = res.data ?? res;
      setSuggestions(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  async function handleAccept(suggestion) {
    try {
      await apiClient.post(`${API_PREFIX}/promotions/${suggestion.id}/accept`, {});
      setSuggestions((prev) => prev.filter((s) => s.id !== suggestion.id));
    } catch (err) {
      setError(err.message || 'Accept failed');
    }
  }

  async function handleReject(suggestion) {
    try {
      await apiClient.post(`${API_PREFIX}/promotions/${suggestion.id}/reject`, {});
      setSuggestions((prev) => prev.filter((s) => s.id !== suggestion.id));
    } catch (err) {
      setError(err.message || 'Reject failed');
    }
  }

  if (loading) {
    return <div className="text-[var(--aif-text-muted)] text-sm animate-pulse" data-testid="promotions-loading">{t('common.loading', 'Lade...')}</div>;
  }
  if (error) {
    return <div className="text-[var(--aif-color-danger)] text-sm">{error}</div>;
  }
  if (suggestions.length === 0) {
    return (
      <div className="text-[var(--aif-text-muted)] text-sm py-3 text-center" data-testid="promotions-empty">
        {t('brainDump.noPromotions', 'Keine Vorschl\u00E4ge.')}
        {!embedded && (
          <span> {t('brainDump.promotionHint', 'Erfasse mehr Notizen \u2014 das System lernt mit der Zeit dazu.')}</span>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="promotions-list">
      {suggestions.map((s) => {
        const entityName = s.suggested_entity_data?.name || 'Unbekannt';
        const entityType = s.suggested_entity_type;
        const icon = ENTITY_ICONS[entityType] || '\uD83D\uDCA1';

        return (
          <div
            key={s.id}
            className="bg-[var(--aif-color-warning-light)] border border-[var(--aif-color-warning-border)] rounded-lg p-4"
            data-testid={`promotion-${s.id}`}
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl">{icon}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[var(--aif-text-primary)] mb-1">
                  {entityName} {t('brainDump.asEntity', 'als')} <span className="font-bold">{entityType}</span> {t('brainDump.createQuestion', 'anlegen?')}
                </div>
                {s.reasoning && (
                  <p className="text-xs text-[var(--aif-text-muted)] mb-3">{s.reasoning}</p>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleAccept(s)}
                    className="px-3 py-1 bg-[var(--aif-color-primary)] hover:opacity-90 text-white text-sm rounded transition min-h-[32px]"
                    data-testid={`accept-${s.id}`}
                  >
                    {t('brainDump.acceptPromotion', 'Anlegen')}
                  </button>
                  <button
                    onClick={() => handleReject(s)}
                    className="px-3 py-1 text-[var(--aif-text-secondary)] hover:bg-[var(--aif-bg-surface)] text-sm rounded transition min-h-[32px]"
                    data-testid={`reject-${s.id}`}
                  >
                    {t('brainDump.rejectPromotion', 'Nein, danke')}
                  </button>
                </div>
              </div>
              {s.confidence != null && (
                <div className="text-xs text-[var(--aif-text-muted)]">
                  {Math.round(s.confidence * 100)}%
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
